import Input from "@/components/form/input/InputField";
import {
  paymentMethods,
  reconcilesViaBank,
  PAYMENTS_EXCEED_MSG,
  type PaymentDraft,
  type UseSalePayments,
} from "@/hooks/useSalePayments";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

interface Props {
  pay: UseSalePayments;
  invoiceTotal: number;
  formatCurrency: (n: number) => string;
}

// The "Payment received" split-tender block, shared across all sale modals.
// Render it only when a buyer is selected on a create form (the caller gates).
export default function SalePaymentSection({ pay, invoiceTotal, formatCurrency }: Props) {
  const { payments, receivedSum, outstanding, exceedsTotal, addPayment, removePayment, updatePayment } = pay;
  return (
    <div
      className="rounded-2xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-800 dark:bg-white/[0.03]"
      data-testid="payment-section"
    >
      <div className="mb-3 text-sm font-semibold text-gray-800 dark:text-white/90">Payment received</div>

      <div className="mb-3 grid grid-cols-3 gap-2 text-center">
        <div className="rounded-lg border border-gray-200 bg-white px-2 py-2 dark:border-gray-800 dark:bg-gray-900">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Invoice</div>
          <div className="text-sm font-bold tabular-nums text-gray-800 dark:text-white/90">{formatCurrency(invoiceTotal)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-2 py-2 dark:border-gray-800 dark:bg-gray-900">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Received</div>
          <div className="text-sm font-bold tabular-nums text-success-600 dark:text-success-400" data-testid="payment-received-total">{formatCurrency(receivedSum)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white px-2 py-2 dark:border-gray-800 dark:bg-gray-900">
          <div className="text-[10px] uppercase tracking-wide text-gray-400">Outstanding</div>
          <div className="text-sm font-bold tabular-nums text-error-600 dark:text-error-400" data-testid="payment-outstanding">{formatCurrency(outstanding)}</div>
        </div>
      </div>

      {payments.map((p: PaymentDraft, i: number) => (
        <div key={i} className="mb-2">
          <div className="grid grid-cols-[1fr_110px_36px] gap-2">
            <select
              value={p.method}
              onChange={(e) => updatePayment(i, { method: e.target.value as PaymentDraft["method"] })}
              className={selectClass}
              data-testid={`payment-method-${i}`}
            >
              {paymentMethods.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <Input
              type="number"
              value={p.amount}
              onChange={(e) => updatePayment(i, { amount: e.target.value })}
              placeholder="Amount"
              min={0}
              step="1"
              data-testid={`payment-amount-${i}`}
            />
            <button
              type="button"
              onClick={() => removePayment(i)}
              aria-label="Remove payment"
              className="h-11 rounded-lg border border-gray-300 text-gray-400 hover:text-error-500 dark:border-gray-700"
            >
              ✕
            </button>
          </div>
          {reconcilesViaBank(p.method) ? (
            <Input
              value={p.reference}
              onChange={(e) => updatePayment(i, { reference: e.target.value })}
              placeholder="Reference (UPI id / cheque no) — matches the bank statement"
              className="mt-1 text-xs"
              data-testid={`payment-reference-${i}`}
            />
          ) : (
            <p className="mt-1 pl-1 text-[11px] text-success-600 dark:text-success-400">In hand · never on the statement</p>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addPayment}
        className="mt-1 w-full rounded-lg border border-dashed border-brand-400 py-2 text-sm font-medium text-brand-500 hover:bg-brand-50 dark:border-brand-500/40 dark:hover:bg-brand-500/10"
        data-testid="add-payment"
      >
        + Add payment method
      </button>

      {exceedsTotal ? (
        <p className="mt-2 text-xs font-medium text-error-500">{PAYMENTS_EXCEED_MSG}</p>
      ) : (
        receivedSum > 0 && outstanding > 0 && (
          <p className="mt-2 text-xs text-warning-600 dark:text-warning-400">
            {formatCurrency(outstanding)} stays outstanding — settle later by tagging the bank statement.
          </p>
        )
      )}
    </div>
  );
}
